import { useState } from 'react';
import { createInvitation } from '../services/apiServices';
import { EMAIL_REQUIRED, INVALID_EMAIL_FORMAT, ROLE_REQUIRED } from '../constants/errorMessages';
import { useFeedbackContext } from '../context';

/**
 * Custom hook to manage the state, validation, and submission logic for the invite form.
 *
 * @returns {object} - Returns an object containing:
 * - `inviteEmail` {string}: The current value of the invite email input field.
 * - `setInviteEmail` {function}: Function to update the invite email value.
 * - `selectedRole` {string}: The currently selected role for the invitation.
 * - `setSelectedRole` {function}: Function to update the selected role.
 * - `isLoadingInvite` {boolean}: Indicates whether the invite submission is in progress.
 * - `errors` {object}: An object containing validation error messages for the form fields.
 * - `handleSubmit` {function}: A function to handle form submission and validate input.
 */
const useInviteForm = () => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const [errors, setErrors] = useState({});

  const { showToast } = useFeedbackContext();

  const validateForm = () => {
    const validationErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!inviteEmail) validationErrors.email = EMAIL_REQUIRED;
    else if (!emailRegex.test(inviteEmail)) validationErrors.email = INVALID_EMAIL_FORMAT;

    if (!selectedRole) validationErrors.role = ROLE_REQUIRED;

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const sendInvitation = async () => {
    const response = await createInvitation(inviteEmail, selectedRole?.value);
    if ([200, 201, 204].includes(response.status)) {
      return response.data;
    } else {
      throw new Error('Error sending invitation');
    }
  };

  const resetFormFields = () => {
    setInviteEmail('');
    setSelectedRole('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isFormValid = validateForm();

    if (!isFormValid) return;

    setErrors({});
    setIsLoadingInvite(true);

    try {
      await sendInvitation();
      showToast({
        variant: 'success',
        title: 'Invitation Sent',
        copy: 'The invitation has been successfully sent.',
      });
      resetFormFields();
    } catch (error) {
      showToast({
        variant: 'error',
        title: "We couldn't send the invitation",
        copy: 'Please try again later.',
      });
    } finally {
      setIsLoadingInvite(false);
    }
  };

  return {
    inviteEmail,
    setInviteEmail,
    selectedRole,
    setSelectedRole,
    isLoadingInvite,
    errors,
    handleSubmit,
  };
};

export default useInviteForm;
